import { Predicate } from 'effect';
import type { CaseAbility } from '@/abilities/createCaseAbility';
import type { DestinationAction, DestinationTree } from '@/abilities/destination';

/** A tab of company settings — a destination under `root`; what a tab holds are its sections, `<tab>.<section>`. */
export type CompanySettingsTabKey =
  | 'general'
  | 'users'
  | 'integrations'
  | 'applications'
  | 'vendorPreferences'
  | 'approvals'
  | 'bankFeed';

type GeneralSectionKey = 'main' | 'features';

type VendorPreferencesSectionKey = 'general' | 'policies';

type ApprovalsSectionKey = 'approvers' | 'basicGroups' | 'advancedGroups' | 'vendorBased' | 'features';

type BankFeedSectionKey = 'connections' | 'synchronization' | 'usersAndPermissions';

/** The settings destinations the Bank Feed rule set answers for: a case that borrows only that set may ask only these. */
export type CompanySettingsBankFeedDestinationKey = 'bankFeed' | `bankFeed.${BankFeedSectionKey}`;

export type CompanySettingsDestinationKey =
  | 'root'
  | CompanySettingsTabKey
  | `general.${GeneralSectionKey}`
  | `vendorPreferences.${VendorPreferencesSectionKey}`
  | `approvals.${ApprovalsSectionKey}`
  | `bankFeed.${BankFeedSectionKey}`;

/**
 * Each container's children, in landing order — the order its address looks for an open child. The order shown on
 * screen is the page's own list; reordering that one never moves where a user lands.
 */
export const companySettingsDestinationTree: DestinationTree<CompanySettingsDestinationKey> = {
  root: ['general', 'users', 'integrations', 'applications', 'vendorPreferences', 'approvals', 'bankFeed'],
  general: ['general.main', 'general.features'],
  vendorPreferences: ['vendorPreferences.general', 'vendorPreferences.policies'],
  approvals: [
    'approvals.approvers',
    'approvals.basicGroups',
    'approvals.advancedGroups',
    'approvals.vendorBased',
    'approvals.features',
  ],
  bankFeed: ['bankFeed.connections', 'bankFeed.usersAndPermissions', 'bankFeed.synchronization'],
};

export const COMPANY_SETTINGS_DESTINATION = 'CompanySettingsDestination';

export type CompanySettingsDestination = {
  readonly kind: typeof COMPANY_SETTINGS_DESTINATION;
  readonly destination: CompanySettingsDestinationKey;
};

export type CompanySettingsAction = DestinationAction;

export type CompanySettingsAbility = CaseAbility<CompanySettingsAction, CompanySettingsDestination>;

export const toCompanySettingsDestination = (
  destination: CompanySettingsDestinationKey,
): CompanySettingsDestination => ({
  kind: COMPANY_SETTINGS_DESTINATION,
  destination,
});

export const isCompanySettingsDestination = (value: unknown): value is CompanySettingsDestination =>
  Predicate.hasProperty(value, 'kind') && value.kind === COMPANY_SETTINGS_DESTINATION;
